import { ZfInput, ZfTypography } from '@zafire/react';
import type { FunctionComponent, KeyboardEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ILiquidityCalculatorAmountInputProps {
    value?: number | null;
    currency: string;
    onValueChange?: (amount: number) => void;
    stopEditing?: (suppressNavigateAfterEdit?: boolean) => void;
}

export const LiquidityCalculatorAmountInput: FunctionComponent<ILiquidityCalculatorAmountInputProps> = ({ value, currency, onValueChange, stopEditing }) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState(value?.toString() ?? '0');

    const resetInput = () => setInputValue(value?.toString() ?? '0');

    const submitInputValue = () => {
        const parsedAmount = Number(inputValue.replace(/[\s,']/g, ''));
        // Cash buffer can never be negative: ignore any negative that slipped past the input sanitizing.
        const nonNegativeAmount = Number.isNaN(parsedAmount) ? parsedAmount : Math.max(0, parsedAmount);
        if (!Number.isNaN(nonNegativeAmount) && nonNegativeAmount !== value) onValueChange?.(nonNegativeAmount);

        stopEditing?.();
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            submitInputValue();
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            resetInput();
            stopEditing?.(true);
        }
    };

    return (
        <ZfInput
            type="number"
            format={true}
            min={0}
            value={inputValue}
            placeholder={t('liquidityCalculatorTable.position.cashBufferInputPlaceholder')}
            onInputChange={(e) => setInputValue(e.detail.replace(/-/g, ''))}
            onBlur={submitInputValue}
            onKeyDown={handleInputKeyDown}
            style={{ width: '100%' }}
        >
            <ZfTypography slot="leading" variant="label-medium">
                {currency}
            </ZfTypography>
        </ZfInput>
    );
};
